import { FocusEvent, Fragment, useState } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Form, { ErrorMessage, Field, FormFooter, FormSection, HelperMessage } from '@atlaskit/form';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import { RadioGroup } from '@atlaskit/radio';
import { OptionsPropType } from '@atlaskit/radio/types';
import TextField from '@atlaskit/textfield';

import { Button } from '@/components/wpl/button/src/button';
import { wacToken } from '@/libraries/design/foundations';
import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href';
import { Row } from '@/libraries/wpl/foundations/src/grid';
import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest';

import { ConsentHubClientConfigProvider } from '../../../../../analytics/consent-hub/consent-client';
import { useLoggedInUser } from '../../../../../utils/logged-in-user-provider';
import { ConsentInfo } from '../components/consent-info';
import { NumberOfUsersInput } from '../components/number-of-users-dropdown';
import { ProductSelector, SelectedProductProvider } from '../components/product-selector.tsx';
import { QuestionField } from '../components/your-question';
import {
	ATLASSIAN_ISOLATED_CLOUD_PRODUCT,
	getProductPlans,
	isAtlassianIsolatedCloudContactFormEnabled,
	isEmailFormat,
	isPersonalDomain,
	submitContactForm,
} from '../utils';

import PricingQuotesResourceCards from './pq-resource-cards';

const containerStyle = css({
	color: wacToken('color.neutral.light900'),
	boxSizing: 'border-box',
	display: 'flex',
	flexWrap: 'wrap',
	justifyContent: 'space-between',
	padding: '16px',
	fontFamily:
		'Charlie Text, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Noto Sans, Ubuntu, Droid Sans, Helvetica Neue, sans-serif',
	fontWeight: '500',
	lineHeight: '24px',
});

const innerContainerStyle = css({
	alignContent: 'flex-start',
	boxSizing: 'border-box',
	display: 'flex',
	flexBasis: '0%',
	flexGrow: '2',
	flexShrink: '1',
	flexWrap: 'wrap',
	minWidth: '50%',
	maxWidth: '620px',
});

const formStyle = css({
	// Note: Existing tech debt, should be fixed
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'[class*=-Field]': {
		fontFamily:
			"'Charlie Text', -apple-system, 'system-ui', 'Segoe UI', Roboto, 'Noto Sans', Ubuntu, 'Droid Sans', 'Helvetica Neue', sans-serif",
		fontSize: '1rem',
		lineHeight: '2.5rem',
		color: wacToken('color.secondary.red'),
		padding: '8px 0 8px',
	},
	// Note: Existing tech debt, should be fixed
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'[class*=-FormFooter]': {
		display: 'flex',
		flexDirection: 'column',
	},
	width: '100%',
	// Note: Existing tech debt, should be fixed
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'[for*=uid]': {
		fontWeight: '500',
		fontSize: '1.25rem',
		fontFamily:
			"'Charlie Text', -apple-system, 'system-ui', 'Segoe UI', Roboto, 'Noto Sans', Ubuntu, 'Droid Sans', 'Helvetica Neue', sans-serif",
	},
	// Note: Existing tech debt, should be fixed
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'[id*=error]': {
		fontFamily:
			"'Charlie Text', -apple-system, 'system-ui', 'Segoe UI', Roboto, 'Noto Sans', Ubuntu, 'Droid Sans', 'Helvetica Neue', sans-serif",
		fontSize: '1rem',
		color: wacToken('color.secondary.red'),
		lineHeight: '24px',
		// Note: Existing tech debt, should be fixed
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > span': {
			display: 'none',
		},
	},
	// Note: Existing tech debt, should be fixed
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'[id*=helper]': {
		fontFamily:
			"'Charlie Text', -apple-system, 'system-ui', 'Segoe UI', Roboto, 'Noto Sans', Ubuntu, 'Droid Sans', 'Helvetica Neue', sans-serif",
		fontSize: '1rem',
	},
	// Note: Existing tech debt, should be fixed
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'[type=number]': {
		fontSize: '1rem',
	},
	// Note: Existing tech debt, should be fixed
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.row-layout': {
		marginBlockStart: '24px',
		paddingInlineStart: 0,
		paddingInlineEnd: 0,
	},
	// Note: Existing tech debt, should be fixed
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'[role=presentation]': {
		backgroundColor: wacToken('color.neutral.light100'),
		borderColor: 'transparent',
		borderRadius: '5px',
		lineHeight: '2.5rem',
		// Note: Existing tech debt, should be fixed
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > input': {
			fontFamily:
				"'Charlie Text', -apple-system, 'system-ui', 'Segoe UI', Roboto, 'Noto Sans', Ubuntu, 'Droid Sans', 'Helvetica Neue', sans-serif",
			fontWeight: '400',
			fontSize: '1.1rem',
			lineHeight: '2.5rem',
		},
	},
	// Note: Existing tech debt, should be fixed
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'[name=opportunity_Description__c]': {
		backgroundColor: `${wacToken('color.neutral.light100')} !important`,
		borderColor: 'transparent !important',
		fontSize: '1.1rem',
		lineHeight: '2rem',
		borderRadius: '5px',
		resize: 'both',
	},
	// Note: Existing tech debt, should be fixed
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'[role=radiogroup]': {
		// Note: Existing tech debt, should be fixed
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > label': {
			alignItems: 'center',
		},
	},
	// Note: Existing tech debt, should be fixed
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.product-selector': {
		paddingInlineStart: 0,
		paddingInlineEnd: 0,
		// Note: Existing tech debt, should be fixed
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		button: {
			backgroundColor: wacToken('color.neutral.light100'),
			borderColor: 'transparent',
			lineHeight: '2.5rem',
			height: '2.5rem',
			fontSize: '1.5rem',
		},
		// Note: Existing tech debt, should be fixed
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > div': {
			// Note: Existing tech debt, should be fixed
			// eslint-disable-next-line local/ensure-wac-token-usage
			boxShadow: '1px 2px 5px rgba(5,3,3,.4392156863)',
			zIndex: '2', // so it doesn't overlap the header with z-index: 3
			transition: 'max-height .4s',
			overflow: 'hidden',
			opacity: '1',
			width: '99%',
		},
	},
	// Note: Existing tech debt, should be fixed
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.post-submit': {
		display: 'none',
	},
	// Note: Existing tech debt, should be fixed
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.post-submit-error': {
		display: 'block',
		// Note: Existing tech debt, should be fixed
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		p: {
			color: wacToken('color.secondary.red'),
		},
	},
});

const textContainer = css({
	marginBlockStart: '5px',
	marginBlockEnd: '3px',
	fontSize: '20px',
	fontWeight: '400',
	letterSpacing: '0.3px',
	lineHeight: '28px',
	verticalAlign: 'baseline',
	color: wacToken('color.neutral.light900'),
});

const PricingQuotes = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [fieldHasError, setFieldHasError] = useState<{
		[x: string]: boolean;
	}>({});
	const [errorMessageText, setErrorMessageText] = useState('');
	const [selectedProduct, setSelectedProduct] = useState('');
	const [changeContactInfo, setChangeContactInfo] = useState(false);
	const [isInvalid, setIsInvalid] = useState(true);

	const intl = useIntl();
	const userInfo = useLoggedInUser();

	const redirectPath = useLocalizedHref(`/enterprise/contact-thanks?formType=pricing-quotes`);

	const NEXT_PUBLIC_IDENTITY_BASE_URL = process.env['NEXT_PUBLIC_IDENTITY_BASE_URL'];
	const getSwitchAccountURL = (continueUrlParam?: string) => {
		const continueUrl = continueUrlParam ? continueUrlParam : window.location.href;
		return `${NEXT_PUBLIC_IDENTITY_BASE_URL}/login/select-account?continue=${encodeURIComponent(
			continueUrl,
		)}`;
	};

	const getOrAsk = intl.formatMessage({
		id: 'pricingQuotes.getOrAsk',
		defaultMessage:
			'Get a quote or ask pricing questions about a product or service you might purchase.',
	});

	const yourCompanyEmail =
		intl.formatMessage({
			id: 'pricingQuotes.yourCompanyEmail',
			defaultMessage: 'Your company email',
		}) + ' *';
	const personalEmailDomainsError = intl.formatMessage({
		id: 'pricingQuotes.personalEmailDomainsError',
		defaultMessage: 'Personal email domains will result in an error.',
	});

	const conditionalFields = (
		/* eslint-disable @typescript-eslint/no-explicit-any */
		product: string,
		getState: any,
		setFieldValue: (name: string, value: any) => void,
		// eslint-disable-next-line complexity
	) => {
		const options: OptionsPropType = getProductPlans(selectedProduct);
		const numberOfPeopleSupported =
			intl.formatMessage({
				id: 'pricingQuotes.numberOfPeople',
				defaultMessage: 'Number of people your agents will support',
			}) + ' *';
		const numberOfDevelopersOnTeam =
			intl.formatMessage({
				id: 'pricingQuotes.numberOfDevelopers',
				defaultMessage: 'Number of developers on your team',
			}) + ' *';
		const numberOfPeopleOnTeam =
			intl.formatMessage({
				id: 'pricingQuotes.numberOfPeopleOnTeam',
				defaultMessage: 'Number of people on your team',
			}) + ' *';
		const numberOfAssets =
			intl.formatMessage({
				id: 'productFeatures.numberOfAssets',
				defaultMessage: 'Number of Assets you want to store',
			}) + ' *';
		const numberOfAgentConversations =
			intl.formatMessage({
				id: 'productFeatures.numberOfAgentConversations',
				defaultMessage: 'Estimated number of virtual service agent conversations per month',
			}) + ' *';

		const enterpriseRolloutTWC = checkFeatureFlagGate(featureFlags.gwpEnterpriseRolloutGateTwc);
		const serviceCollectionEnabled = checkFeatureFlagGate(
			featureFlags.gwp8165EnterpriseContactServiceCollection,
		);
		const backupRestoreEnabled = checkFeatureFlagGate(featureFlags.gwp9763BackupRestoreContactForm);
		const additionalSandboxEnabled = checkFeatureFlagGate(
			featureFlags.gwp9608AdditionalSandboxContactForm,
		);
		const atlassianIsolatedCloudEnabled = isAtlassianIsolatedCloudContactFormEnabled();
		const sunsetCompass = checkFeatureFlagGate(featureFlags.gwp1072SunsetCompass);
		const addDX = checkFeatureFlagGate(featureFlags.gwp12021AddDX);

		const productsWithPlans = [
			'Jira Software',
			'Jira',
			'Confluence',
			'Jira Service Management',
			'Bitbucket',
			'Atlassian Guard',
			'Jira Product Discovery',
			...(!sunsetCompass ? ['Compass'] : []),
			'Loom',
			...(addDX ? ['DX'] : []),
		];
		if (enterpriseRolloutTWC) productsWithPlans.push('Teamwork Collection');
		if (serviceCollectionEnabled) productsWithPlans.push('Service Collection');
		if (backupRestoreEnabled) {
			productsWithPlans.push('Jira Cloud Backup and Restore');
			productsWithPlans.push('Jira Service Management Cloud Backup and Restore');
			productsWithPlans.push('Confluence Cloud Backup and Restore');
		}
		if (additionalSandboxEnabled) {
			productsWithPlans.push('Jira Additional Sandbox');
			productsWithPlans.push('Confluence Additional Sandbox');
			productsWithPlans.push('Jira Service Management Additional Sandbox');
			productsWithPlans.push('Jira Product Discovery Additional Sandbox');
		}

		return (
			<FormSection>
				{productsWithPlans.includes(product) && getState().values['platform'] === 'Cloud' && (
					<Field
						name={'plan'}
						label={
							intl.formatMessage({
								id: 'pricingQuotes.plan',
								defaultMessage: 'Which plan are you interested in?',
							}) + ' *'
						}
					>
						{({ fieldProps, error }) => (
							<Fragment>
								<RadioGroup
									{...fieldProps}
									options={options}
									onChange={(edition) => {
										const value = edition.currentTarget.value;
										setFieldValue('edition', value);
										setFieldValue('plan', value);
									}}
								/>
								{error && <ErrorMessage>{planErrorMessage}</ErrorMessage>}
							</Fragment>
						)}
					</Field>
				)}
				{[
					'Jira Software',
					'Jira',
					'Confluence',
					'Jira Service Management',
					'Bitbucket',
					'Jira Work Management',
					'Atlassian Access',
					'Atlassian Guard',
					'Opsgenie',
					'Trello',
					'Jira Align',
					'Jira Product Discovery',
					...(!sunsetCompass ? ['Compass'] : []),
					'Atlas',
					'Loom',
					'Jira Service Management Assets',
					'Jira Service Management Virtual Service Agent',
					'Teamwork Collection',
					'Strategy Collection',
					'Service Collection',
					'Confluence Cloud Backup and Restore',
					'Jira Cloud Backup and Restore',
					'Jira Service Management Cloud Backup and Restore',
					'Confluence Additional Sandbox',
					'Jira Additional Sandbox',
					'Jira Service Management Additional Sandbox',
					'Jira Product Discovery Additional Sandbox',
					...(addDX ? ['DX'] : []),
					...(atlassianIsolatedCloudEnabled ? [ATLASSIAN_ISOLATED_CLOUD_PRODUCT] : []),
				].includes(product) && (
					<Field
						name={'numberOfUsers'}
						label={
							product === 'Jira Service Management'
								? numberOfPeopleSupported
								: product === 'Jira Align' ||
									  product === 'Jira Product Discovery' ||
									  (!sunsetCompass && product === 'Compass') ||
									  product === 'Atlas' ||
									  product === 'Confluence Additional Sandbox' ||
									  product === 'Jira Additional Sandbox' ||
									  product === 'Jira Service Management Additional Sandbox' ||
									  product === 'Jira Product Discovery Additional Sandbox'
									? numberOfDevelopersOnTeam
									: product === 'Jira Service Management Assets'
										? numberOfAssets
										: product === 'Jira Service Management Virtual Service Agent'
											? numberOfAgentConversations
											: numberOfPeopleOnTeam
						}
					>
						{({ fieldProps, error }) => (
							<Fragment>
								<NumberOfUsersInput
									{...fieldProps}
									onChangeHandler={(numberOfUsers) => {
										setFieldValue('numberOfUsers', Number(numberOfUsers));
									}}
								/>
								{error && (
									<ErrorMessage>
										{product === 'Jira Service Management'
											? numberOfPeopleJSMErrorMessage
											: product === 'Jira Service Management Assets' ||
												  product === 'Jira Service Management Virtual Service Agent'
												? genericRequiredFieldErrorMessage
												: numberOfPeopleOnTeamErrorMessage}
									</ErrorMessage>
								)}
							</Fragment>
						)}
					</Field>
				)}
			</FormSection>
		);
	};

	const handleBlurEvent = (event: FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
		const { name, value } = event.target;

		if (name === 'email') {
			if (!value) {
				setFieldHasError({ ...fieldHasError, [name]: true });
				setErrorMessageText('NOT_EMAIL');
				setIsInvalid(true);
			} else {
				if (isEmailFormat(value) && !isPersonalDomain(value)) {
					setFieldHasError({ ...fieldHasError, [name]: false });
					setErrorMessageText('IS_VALID');
					setIsInvalid(false);
				} else {
					setFieldHasError({ ...fieldHasError, [name]: true });
					setIsInvalid(true);
					if (!isEmailFormat(value)) {
						setErrorMessageText('NOT_EMAIL');
					} else if (isPersonalDomain(value)) {
						setErrorMessageText('PERSONAL_DOMAIN');
					}
				}
			}
		} else {
			setFieldHasError({ ...fieldHasError, [name]: !value });
			setIsInvalid(!value);
		}
	};

	/*
        Error Messages
    */

	const personalDomainErrorMessage = intl.formatMessage({
		id: 'pricingQuotes.personalDomainErrorMessage',
		defaultMessage: 'Enter an email with a company domain.',
	});

	const notEmailErrorMessage = intl.formatMessage({
		id: 'pricingQuotes.notEmailErrorMessage',
		defaultMessage: 'Enter a valid email address.',
	});

	const jobTitleErrorMessage = intl.formatMessage({
		id: 'pricingQuotes.jobTitleErrorMessage',
		defaultMessage: 'Your job title is required.',
	});

	const firstNameErrorMessage = intl.formatMessage({
		id: 'pricingQuotes.firstNameErrorMessage',
		defaultMessage: 'Your first name is required.',
	});

	const lastNameErrorMessage = intl.formatMessage({
		id: 'pricingQuotes.lastNameErrorMessage',
		defaultMessage: 'Your last name is required.',
	});

	const companyErrorMessage = intl.formatMessage({
		id: 'pricingQuotes.companyErrorMessage',
		defaultMessage: 'Your company name is required.',
	});

	const productErrorMessage = intl.formatMessage({
		id: 'pricingQuotes.productErrorMessage',
		defaultMessage: 'Select a product or service to continue.',
	});

	const planErrorMessage = intl.formatMessage({
		id: 'pricingQuotes.planErrorMessage',
		defaultMessage: 'Select any cloud plan to continue.',
	});

	const numberOfPeopleOnTeamErrorMessage = intl.formatMessage({
		id: 'pricingQuotes.numberOfPeopleOnTeamErrorMessage',
		defaultMessage: 'Your team size is required.',
	});

	const numberOfPeopleJSMErrorMessage = intl.formatMessage({
		id: 'pricingQuotes.numberOfPeopleSupportedErrorMessage',
		defaultMessage: 'We need to know how many people contact your service desk.',
	});

	const genericRequiredFieldErrorMessage = intl.formatMessage({
		id: 'pricingQuotes.genericRequiredFieldErrorMessage',
		defaultMessage: 'This field is required.',
	});

	const HandleSubmit = (form: HTMLFormElement) => {
		const errors: { [field: string]: boolean } = {};

		// eslint-disable-next-line @typescript-eslint/no-for-in-array
		for (const field in form) {
			if (form[field] === undefined || fieldHasError[field]) {
				const errorFields = [
					'email',
					'firstName',
					'lastName',
					'company',
					'title',
					'plan',
					'numberOfUsers',
					'opportunity_Description__c',
				];
				if (errorFields.includes(field)) {
					errors[field] = true;
				}
			}
		}

		// Product is validated against selectedProduct React state rather than
		// form["product"], because final-form only enumerates fields that have
		// a validate prop — setFieldValue/onChange alone won't make it appear
		// in the for...in loop above. selectedProduct is reliably set by both
		// manual selection and URL preselection (?product= query param).
		// We also write it into form so submitContactForm includes it in the payload.
		if (!selectedProduct) {
			errors['product'] = true;
		} else {
			form['product'] = selectedProduct;
		}

		if (Object.keys(errors).length > 0) {
			return errors;
		} else {
			return submitContactForm(form, 'pricing-quotes', createAnalyticsEvent)
				.then(() => {
					window.location.assign(redirectPath);
				})
				.catch(() => {
					document
						.querySelector('.post-submit')
						?.classList.replace('post-submit', 'post-submit-error');
				});
		}
	};

	const pricingQuoteSubmitText = intl.formatMessage({
		id: 'pricingQuotes.submit',
		defaultMessage: 'Submit',
	});

	const loggedOutView = (
		<ConsentHubClientConfigProvider>
			<SelectedProductProvider selectedProduct={selectedProduct}>
				<div css={containerStyle}>
					<div css={innerContainerStyle}>
						<div css={textContainer}>
							<p>{getOrAsk}</p>
						</div>
						<Form onSubmit={HandleSubmit}>
							{({ formProps, setFieldValue, getState }) => (
								<form {...formProps} css={formStyle}>
									<FormSection>
										<Field
											name="email"
											label={yourCompanyEmail}
											validate={(value) => {
												setFieldValue('email', value);
											}}
										>
											{({ fieldProps, error }) => (
												<Fragment>
													<TextField
														{...fieldProps}
														value={fieldProps.value || ''}
														onBlur={handleBlurEvent}
													/>
													{(error && (
														<ErrorMessage>
															{errorMessageText === 'PERSONAL_DOMAIN'
																? personalDomainErrorMessage
																: notEmailErrorMessage}
														</ErrorMessage>
													)) || <HelperMessage>{personalEmailDomainsError}</HelperMessage>}
												</Fragment>
											)}
										</Field>
									</FormSection>
									<FormSection>
										<Row layout={'6-6'}>
											<Field
												name={'firstName'}
												label={
													intl.formatMessage({
														id: 'pricingQuotes.forename',
														defaultMessage: 'First name',
													}) + ' *'
												}
											>
												{({ fieldProps, error }) => (
													<Fragment>
														<TextField {...fieldProps} value={fieldProps.value || ''} />
														{error && <ErrorMessage>{firstNameErrorMessage}</ErrorMessage>}
													</Fragment>
												)}
											</Field>
											<Field
												name={'lastName'}
												label={
													intl.formatMessage({
														id: 'pricingQuotes.surname',
														defaultMessage: 'Last name',
													}) + ' *'
												}
											>
												{({ fieldProps, error }) => (
													<Fragment>
														<TextField {...fieldProps} value={fieldProps.value || ''} />
														{error && <ErrorMessage>{lastNameErrorMessage}</ErrorMessage>}
													</Fragment>
												)}
											</Field>
										</Row>
										<Row layout={'6-6'}>
											<Field
												name={'company'}
												label={
													intl.formatMessage({
														id: 'pricingQuotes.company',
														defaultMessage: 'Company',
													}) + ' *'
												}
											>
												{({ fieldProps, error }) => (
													<Fragment>
														<TextField {...fieldProps} value={fieldProps.value || ''} />
														{error && <ErrorMessage>{companyErrorMessage}</ErrorMessage>}
													</Fragment>
												)}
											</Field>
											<Field
												name={'title'}
												label={
													intl.formatMessage({
														id: 'pricingQuotes.title',
														defaultMessage: 'Job title',
													}) + ' *'
												}
											>
												{({ fieldProps, error }) => (
													<Fragment>
														<TextField {...fieldProps} value={fieldProps.value || ''} />
														{error && <ErrorMessage>{jobTitleErrorMessage}</ErrorMessage>}
													</Fragment>
												)}
											</Field>
										</Row>
									</FormSection>
									<FormSection>
										<Field
											name={'product'}
											label={
												intl.formatMessage({
													id: 'pricingQuotes.product',
													defaultMessage: 'Which product or service are you inquiring about?',
												}) + ' *'
											}
										>
											{({ fieldProps, error }) => (
												<Fragment>
													<ProductSelector
														{...fieldProps}
														pageType="pricing-quotes"
														onChangeHandler={(product, platform) => {
															fieldProps.onChange(product);
															setFieldValue('platform', platform);
															setSelectedProduct(product);
														}}
													/>
													{(error && <ErrorMessage>{productErrorMessage}</ErrorMessage>) || (
														<HelperMessage>
															{intl.formatMessage({
																id: 'pricingQuotes.productHelper',
																defaultMessage:
																	'If you’re asking about multiple products, select the one with the most users.',
															})}
														</HelperMessage>
													)}
												</Fragment>
											)}
										</Field>
									</FormSection>
									{conditionalFields(selectedProduct, getState, setFieldValue)}
									<FormSection>{QuestionField('pricing-quotes', handleBlurEvent)}</FormSection>
									{ConsentInfo(setFieldValue)}
									<FormFooter align={'start'}>
										<div>
											<Button
												inputType={'button'}
												text={pricingQuoteSubmitText}
												aria-label={pricingQuoteSubmitText}
												type="primary"
												color={isInvalid ? 'gray' : 'darkblue'}
												onClick={() => {
													formProps.onSubmit();
												}}
											/>
										</div>
										<div className={'post-submit'}>
											<p>
												{intl.formatMessage({
													id: 'pricingQuotes.submitError',
													defaultMessage: 'An error has occurred. Please try again later.',
												})}
											</p>
										</div>
									</FormFooter>
								</form>
							)}
						</Form>
					</div>
					<PricingQuotesResourceCards />
				</div>
			</SelectedProductProvider>
		</ConsentHubClientConfigProvider>
	);

	const loggedInView = (
		<ConsentHubClientConfigProvider>
			<SelectedProductProvider selectedProduct={selectedProduct}>
				<div css={containerStyle}>
					<div css={innerContainerStyle}>
						<div css={textContainer}>
							<p>{getOrAsk}</p>
						</div>
						<div>
							<Form onSubmit={HandleSubmit}>
								{({ formProps, setFieldValue, getState }) => (
									<form {...formProps} css={formStyle}>
										<p>
											{intl.formatMessage({
												id: 'pricingQuotes.contactInfo',
												defaultMessage: "You're logged in as:",
											})}
										</p>
										<>{setFieldValue('email', userInfo.email)}</>
										<>{setFieldValue('name', userInfo.name)}</>
										<p css={css({ fontSize: '1.25rem' })}>
											{userInfo.name}
											<br />
											{userInfo.email}
										</p>
										<div
											css={css({
												fontSize: '0.75rem',

												color: wacToken('color.extended.blue800'),
												// Note: Existing tech debt, should be fixed
												// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
												a: {
													textDecoration: 'none',
													color: wacToken('color.extended.blue800'),
													marginInlineEnd: '30px',
													display: 'inline-block',
													marginBlockEnd: '1rem',
													// Note: Existing tech debt, should be fixed
													// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
													'[role=presentation]': {
														backgroundColor: 'transparent',
													},
												},
											})}
										>
											<a
												href="#changeContactInfo"
												onClick={() => {
													setChangeContactInfo(true);
												}}
											>
												{intl.formatMessage({
													id: 'pricingQuotes.updateInfo',
													defaultMessage: 'Update contact info',
												})}
												<span
													css={css({
														// Note: Existing tech debt, should be fixed
														// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
														'& > span': {
															lineHeight: 'unset',
														},
													})}
												>
													<ChevronRightIcon label="" />
												</span>
											</a>
											<a href={getSwitchAccountURL()}>
												{intl.formatMessage({
													id: 'pricingQuotes.differentAccount',
													defaultMessage: 'Log in with a different Atlassian account',
												})}
												<span
													css={css({
														// Note: Existing tech debt, should be fixed
														// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
														'& > span': {
															lineHeight: 'unset',
														},
													})}
												>
													<ChevronRightIcon label="" />
												</span>
											</a>
										</div>
										<FormSection>
											<Field
												name={'title'}
												label={
													intl.formatMessage({
														id: 'pricingQuotes.yourJobTitle',
														defaultMessage: 'Your job title',
													}) + ' *'
												}
											>
												{({ fieldProps, error }) => (
													<Fragment>
														<TextField
															{...fieldProps}
															value={fieldProps.value || ''}
															onBlur={handleBlurEvent}
														/>
														{error && <ErrorMessage>{jobTitleErrorMessage}</ErrorMessage>}
														<HelperMessage>
															{intl.formatMessage({
																id: 'pricingQuotes.yourJobTitleHelper',
																defaultMessage:
																	'We ask this to connect you to the best expert for your role.',
															})}
														</HelperMessage>
													</Fragment>
												)}
											</Field>
										</FormSection>
										<FormSection>
											<Field
												name={'product'}
												label={
													intl.formatMessage({
														id: 'pricingQuotes.product',
														defaultMessage: 'Which product or service are you inquiring about?',
													}) + ' *'
												}
											>
												{({ fieldProps }) => (
													<>
														<ProductSelector
															{...fieldProps}
															pageType="pricing-quotes"
															onChangeHandler={(product, platform) => {
																fieldProps.onChange(product);
																setFieldValue('platform', platform);
																setSelectedProduct(product);
															}}
														/>
														<HelperMessage>
															{intl.formatMessage({
																id: 'pricingQuotes.productHelper',
																defaultMessage:
																	'If you’re asking about multiple products, select the one with the most users.',
															})}
														</HelperMessage>
													</>
												)}
											</Field>
										</FormSection>
										{conditionalFields(selectedProduct, getState, setFieldValue)}
										<FormSection>{QuestionField('pricing-quotes', handleBlurEvent)}</FormSection>
										{ConsentInfo(setFieldValue)}
										<FormFooter align={'start'}>
											<div>
												<Button
													inputType={'button'}
													text={pricingQuoteSubmitText}
													aria-label={pricingQuoteSubmitText}
													type="primary"
													color={isInvalid ? 'gray' : 'darkblue'}
													onClick={() => {
														formProps.onSubmit();
													}}
												/>
											</div>
											<div className={'post-submit'}>
												<p>
													{intl.formatMessage({
														id: 'pricingQuotes.submitError',
														defaultMessage: 'An error has occurred. Please try again later.',
													})}
												</p>
											</div>
										</FormFooter>
									</form>
								)}
							</Form>
						</div>
					</div>
					<PricingQuotesResourceCards />
				</div>
			</SelectedProductProvider>
		</ConsentHubClientConfigProvider>
	);

	if (userInfo.isUserLoggedIn) {
		if (userInfo.email) {
			if (
				isPersonalDomain(userInfo.email) ||
				userInfo.email.includes('atlassian.com') ||
				changeContactInfo
			) {
				return loggedOutView;
			} else {
				return loggedInView;
			}
		}
	}

	return loggedOutView;
};

export default PricingQuotes;
